import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useUserRole } from "@/hooks/useUserRole";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings, CreditCard, Activity, ShieldCheck, LogOut, Globe, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/i18n/I18nContext";

export function UserAvatarMenu({ variant = "topbar" }: { variant?: "topbar" | "sidebar" } = {}) {
  const inSidebar = variant === "sidebar";
  const { signOut } = useAuth();
  const { profile, getInitials } = useProfile();
  const { isAdmin } = useUserRole();
  const { t, locale, setLocale, locales } = useI18n();
  const navigate = useNavigate();

  const initials = getInitials();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const menuItems = [
    { label: t("menu.billing"),  icon: CreditCard, path: "/billing"  },
    { label: t("menu.settings"), icon: Settings,   path: "/settings" },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex items-center gap-2 h-9 px-2 rounded-md hover:bg-muted/50 transition-colors outline-none ${inSidebar ? "w-full" : ""}`}>
          <Avatar className="h-7 w-7 border border-primary/20">
            {profile?.avatar_url ? (
              <AvatarImage src={profile.avatar_url} alt={profile.display_name || "User"} />
            ) : null}
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className={`text-sm font-medium text-foreground truncate ${inSidebar ? "flex-1 text-left" : "hidden sm:inline max-w-[120px]"}`}>
            {profile?.display_name || profile?.email || t("menu.userFallback")}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={inSidebar ? "start" : "end"} side={inSidebar ? "top" : "bottom"} className="w-56 bg-card border-border">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">{profile?.display_name || t("menu.userFallback")}</p>
              {isAdmin && <Badge className="text-[8px] bg-primary/20 text-primary border-primary/30 px-1">{t("menu.admin")}</Badge>}
            </div>
            <p className="text-xs text-muted-foreground truncate">{profile?.email || ""}</p>
            {/* Credits live here (and in Billing), not on the dashboard hero. */}
            {profile?.acu_balance != null && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <CreditCard className="h-3 w-3 text-primary/60" />
                <span className="tabular-nums">{profile.acu_balance.toLocaleString()} credits</span>
                {profile.plan && <span className="text-muted-foreground/50 capitalize">· {profile.plan}</span>}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {menuItems.map((item) => (
          <DropdownMenuItem
            key={item.label}
            className="cursor-pointer gap-2"
            onClick={() => navigate(item.path)}
          >
            <item.icon className="h-4 w-4 text-muted-foreground" />
            {item.label}
          </DropdownMenuItem>
        ))}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer gap-2"
              onClick={() => navigate("/platform")}
            >
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              {t("menu.platformIntelligence")}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        {/* Languages live in a submenu: the account menu stays five rows tall
            instead of scrolling the identity and actions off-screen. */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1">{t("menu.language")}</span>
            <span className="text-xs text-muted-foreground">
              {locales.find((l) => l.code === locale)?.nativeLabel ?? locale}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-72 overflow-y-auto bg-card border-border">
            {locales.map((l) => (
              <DropdownMenuItem
                key={l.code}
                className="cursor-pointer gap-2"
                onClick={() => setLocale(l.code)}
              >
                <Check className={`h-4 w-4 ${l.code === locale ? "text-primary" : "opacity-0"}`} />
                {l.nativeLabel}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer gap-2 text-destructive focus:text-destructive" onClick={handleSignOut}>
          <LogOut className="h-4 w-4" />
          {t("menu.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
